import { broadPhaseFlat } from './broadPhase'
import type {
  BroadRequest,
  BroadResponse,
  ClashEngine,
  NarrowRequest,
  NarrowResponse,
} from './clashTypes'
import { narrowPhaseBatch } from './narrowPhase'

// ---------------------------------------------------------------------------
// Motor: as duas fases atras de uma interface, mais o protocolo de
// mensagens que o worker fala.
//
// O nucleo (`handleClashRequest`) e uma funcao pura pedido -> resposta.
// O worker e uma casca de dez linhas a volta dela, e o motor local usa a
// mesma funcao. Consequencia pratica: os testes cobrem o mesmo codigo
// que corre no worker, sem precisarem de um Worker verdadeiro.
// ---------------------------------------------------------------------------

export type ClashRequestMessage =
  | { id: number; type: 'broad'; payload: BroadRequest }
  | { id: number; type: 'narrow'; payload: NarrowRequest }

export type ClashResponseMessage =
  | { id: number; type: 'broad'; payload: BroadResponse }
  | { id: number; type: 'narrow'; payload: NarrowResponse }
  | { id: number; type: 'error'; message: string }

export interface HandledClashResponse {
  response: ClashResponseMessage
  /** Buffers a transferir de volta (evita copia). */
  transfer: Transferable[]
}

/** Nucleo do motor. Nunca lanca: erros viram uma resposta `error`. */
export function handleClashRequest(
  msg: ClashRequestMessage,
): HandledClashResponse {
  try {
    if (msg.type === 'broad') {
      const payload = broadPhaseFlat(msg.payload)
      return { response: { id: msg.id, type: 'broad', payload }, transfer: [payload.pairs.buffer] }
    }
    const payload = narrowPhaseBatch(msg.payload)
    return { response: { id: msg.id, type: 'narrow', payload }, transfer: [] }
  } catch (err) {
    return {
      response: {
        id: msg.id,
        type: 'error',
        message: err instanceof Error ? err.message : String(err),
      },
      transfer: [],
    }
  }
}

/** Motor na propria thread. E o que os testes usam e o recurso quando
 *  o browser nao tem workers de modulo. Bloqueia a thread que o chama:
 *  em producao prefira `createWorkerClashEngine`. */
export function createLocalClashEngine(): ClashEngine {
  const run = <T,>(msg: ClashRequestMessage): Promise<T> => {
    const { response } = handleClashRequest(msg)
    if (response.type === 'error') return Promise.reject(new Error(response.message))
    return Promise.resolve(response.payload as T)
  }
  return {
    broadPhase: (req: BroadRequest) => run<BroadResponse>({ id: 0, type: 'broad', payload: req }),
    narrowPhase: (req: NarrowRequest) => run<NarrowResponse>({ id: 0, type: 'narrow', payload: req }),
    dispose: () => {},
  }
}
